import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { KIND, ST, FLAG } from '../core/entities.js';
import { makeRig, PROPORTIONS, BONE_INDEX, BONE_COUNT } from '../chars/rig.js';
import { specFor } from '../chars/variants.js';
import { CLIPS, CLIP_NAMES, CLIP_INDEX, CLIP_COUNT, C, LOCO, IDLES, LOCO_SPEED, sampleClip, wristCompensate } from './clips.js';
import { makeQ, makeP, makeM, eulerToQuat, blendQ, blendP, addQ, poseToMatrices } from './pose.js';
import { bakeVAT } from './vat.js';
import { plantFeet, lookAt, IK_STATS } from './ik.js';

/**
 * ===========================================================================
 *  ANIMATION SYSTEM  —  publishes `world.anim`
 * ===========================================================================
 *
 *   anim.clipId(name)             -> integer id (-1 if unknown)
 *   anim.clipNames                -> string[]
 *   anim.duration(name|id)        -> seconds
 *   anim.events(name|id)          -> frozen marker array (schema in clips.js)
 *   anim.play(id, name, opts)     -> start a clip on an entity
 *        opts: { blend, rate, hold, force, loop }
 *   anim.crossFade(id, name, t)   -> play with an explicit blend time
 *   anim.sample(id, outMat)       -> write 21 * 12 skinning matrices (3x4
 *                                    row-major) for that entity's live pose
 *   anim.speedFor(id)             -> effective playback rate
 *   anim.sampleInstance(id, out5) -> [clipA, tA01, clipB, tB01, blendB] for
 *                                    the crowd's vertex-animation shader
 *   anim.rootMotion(id, out3)     -> world-space root delta accumulated this
 *                                    fixed step (attacks lunge)
 *   anim.onEvent(cb)              -> cb(entityId, marker); also on the bus as
 *                                    'anim:event'
 *   anim.vat                      -> the baked texture + frame ranges
 *   anim.autoDrive                -> presentation-only crowd pantomime
 * ===========================================================================
 */

/* ------------------------------------------------------------------ assets */

const VAT_KINDS = ['grunt', 'archer', 'sergeant', 'officer', 'general', 'hero'];
let ASSETS = null;

/** Rigs + VAT bake. Shared by the anim system and the crowd renderer, whichever
 *  initialises first (the crowd runs at a lower ORDER than the animator). */
export function animAssets(world) {
  if (ASSETS && ASSETS.world === world) return ASSETS;
  const t0 = performance.now();
  const rigs = {}, kinds = [];
  for (const k of VAT_KINDS) {
    const S = specFor(k, 0);
    const rig = makeRig(S);
    rigs[k] = rig;
    kinds.push({ name: k, rig });
  }
  rigs.player = rigs.hero;
  const vat = bakeVAT(kinds, CLIPS, { fps: 30, framesPerRow: 16 });
  ASSETS = { world, rigs, vat, buildMs: +(performance.now() - t0).toFixed(1) };
  return ASSETS;
}

/** entity KIND -> VAT kind index */
export const KIND_TO_VAT = new Int32Array(12);
{
  const m = { [KIND.PLAYER]: 5, [KIND.GRUNT]: 0, [KIND.SERGEANT]: 2, [KIND.OFFICER]: 3,
    [KIND.GENERAL]: 4, [KIND.ALLY]: 0, [KIND.ALLY_OFFICER]: 3, [KIND.ARCHER]: 1,
    [KIND.HORSE]: 0, [KIND.SIEGE]: 0 };
  for (let i = 0; i < 12; i++) KIND_TO_VAT[i] = m[i] ?? 0;
}
export const VAT_KIND_NAMES = VAT_KINDS;

/** Leg length of each entity KIND relative to the 1.80 m clip rig. Clips are
 *  authored on that rig, so a taller body's stride scales with it. */
export const LEG_SCALE = new Float32Array(12);
{
  for (let k = 0; k < 12; k++) {
    const p = PROPORTIONS[VAT_KINDS[KIND_TO_VAT[k]]] || PROPORTIONS.grunt;
    LEG_SCALE[k] = (p.height / 1.80) * (p.legs ?? 1);
  }
}

/* -------------------------------------------------------------- scratch --- */

const eulA = new Float32Array(BONE_COUNT * 3), posA = new Float32Array(BONE_COUNT * 3);
const eulB = new Float32Array(BONE_COUNT * 3), posB = new Float32Array(BONE_COUNT * 3);
const eulD = new Float32Array(BONE_COUNT * 3);
const qA = makeQ(), qB = makeQ(), qO = makeQ(), qD = makeQ();
const pO = makeP();
const mWorld = makeM(), mSkin = makeM();
const _out5 = new Float32Array(5);
const _v3 = new THREE.Vector3();
const _look = new THREE.Vector3();
const ADD_MASK = new Float32Array(BONE_COUNT);

const hash32 = (n) => { n = Math.imul(n ^ (n >>> 15), 0x2c1b3c6d); n = Math.imul(n ^ (n >>> 12), 0x297a2d39); return (n ^ (n >>> 15)) >>> 0; };
const frac = (n) => hash32(n) / 4294967296;
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * Gait phase warp.
 *
 * Walk, run and sprint keep the foot on the ground for different fractions of
 * the cycle. Blend two of them at the same normalised time and you mix a
 * mid-stance pose with a mid-flight one — the blended foot is then planted for
 * neither clip's stance and it creeps. So each clip's clock is warped to put
 * it at the same point of the *blended* gait: stance maps onto stance, flight
 * onto flight. Done per half-cycle so the trailing foot stays exactly half a
 * cycle behind. Cheap: four multiplies, no branches that matter.
 */
function gaitWarp(t, k, kr) {
  const k2 = k * 2, r2 = kr * 2;
  if (k2 >= 0.99 || r2 >= 0.99 || r2 < 1e-3 || Math.abs(k2 - r2) < 1e-4) return t;
  const h = t < 0.5 ? 0 : 0.5;
  const u = (t - h) * 2;
  const v = u <= r2 ? u * (k2 / r2) : k2 + (u - r2) * ((1 - k2) / (1 - r2));
  return h + v * 0.5;
}

/* ---- which clip does a gameplay state want? ---------------------------- */
const ATTACK_CHAIN = [C.attackLight1, C.attackLight2, C.attackLight3, C.attackLight4];

export function createAnimation() {
  const N = 2600;
  const S = {
    clipA: new Int16Array(N), tA: new Float32Array(N),
    clipB: new Int16Array(N), tB: new Float32Array(N),
    fade: new Float32Array(N), fadeDur: new Float32Array(N),
    phase: new Float32Array(N), idleT: new Float32Array(N),
    locoA: new Int16Array(N), locoB: new Int16Array(N), locoW: new Float32Array(N),
    idle: new Int16Array(N),           // which standing stance this unit uses
    mode: new Uint8Array(N),           // 0 = locomotion, 1 = single clip
    hold: new Float32Array(N),         // seconds the single clip owns the body
    rate: new Float32Array(N),
    flinch: new Float32Array(N), flinchV: new Float32Array(N),
    uid: new Int32Array(N),             // recycled-slot ABA guard only
    spawnKey: new Uint32Array(N),       // deterministic presentation identity
    rmX: new Float32Array(N), rmZ: new Float32Array(N),
    lastT: new Float32Array(N),
    autoT: new Float32Array(N),
    jit: new Float32Array(N),          // per-unit clock jitter, crowd de-sync
    lock: new Float32Array(N),   // seconds an explicit play() owns the body
  };
  const listeners = [];
  let api = null, W = null, assets = null;
  let heroBones = null, heroMesh = null, heroRig = null;
  let breatheT = 0;

  /* -------------------------------------------------- per-entity helpers */

  function reset(id, clip) {
    const key = S.spawnKey[id] || (id + 1);
    S.clipA[id] = clip; S.tA[id] = 0;
    S.clipB[id] = clip; S.tB[id] = 0;
    S.fade[id] = 1; S.fadeDur[id] = 0.15;
    S.mode[id] = 0; S.hold[id] = 0; S.rate[id] = 1;
    S.idle[id] = IDLES[hash32(key * 2654435761 + 3) % IDLES.length];
    S.locoA[id] = S.idle[id]; S.locoB[id] = LOCO[1]; S.locoW[id] = 0;
    S.phase[id] = frac(key * 7919 + 13);
    S.idleT[id] = frac(key * 104729 + 7) * CLIPS[S.idle[id]].dur;
    // Two ranks of soldiers must never breathe in unison. Every unit gets its
    // own clock rate and its own pantomime timer, so even units that spawn on
    // the same tick and pick the same clip drift apart within a second.
    S.jit[id] = 0.86 + frac(key * 2246822519 + 91) * 0.30;
    S.flinch[id] = 0; S.flinchV[id] = 0; S.lastT[id] = 0;
    S.autoT[id] = frac(key * 374761393 + 5) * 2.6; S.lock[id] = 0;
    S.rmX[id] = 0; S.rmZ[id] = 0;
  }

  /** Restore all per-entity presentation clocks to a fresh-battle state. */
  function resetBattle() {
    S.uid.fill(0);
    S.spawnKey.fill(0);
    for (let i = 0; i < N; i++) reset(i, LOCO[0]);
    breatheT = 0;
    return api;
  }

  /** Start `clipIdx` on `id`, cross-fading from whatever is playing. */
  function start(id, clipIdx, o) {
    if (clipIdx < 0 || clipIdx >= CLIP_COUNT) return;
    const cur = effectiveClip(id);
    const blend = (o && o.blend != null) ? o.blend : CLIPS[clipIdx].blend;
    if (blend > 0.001 && cur >= 0) {
      S.clipB[id] = cur; S.tB[id] = effectiveTime(id);
      S.fade[id] = 0; S.fadeDur[id] = blend;
    } else { S.fade[id] = 1; }
    S.clipA[id] = clipIdx;
    // A looping clip started by the crowd pantomime MUST NOT begin at frame 0:
    // fifty archers that entered `idle_alert` on the same tick would then hold
    // one identical pose for ever, which is the classic synchronised-crowd
    // tell. One-shots (attacks, reactions) still start at zero — they have to.
    S.tA[id] = (CLIPS[clipIdx].loop && W && id !== W.playerId)
      ? frac(S.spawnKey[id] * 2654435761 + clipIdx * 7919) * CLIPS[clipIdx].dur : 0;
    S.mode[id] = 1;
    S.rate[id] = (o && o.rate) || 1;
    S.hold[id] = (o && o.hold != null) ? o.hold : (CLIPS[clipIdx].loop ? 1e9 : CLIPS[clipIdx].dur);
    S.lock[id] = (o && o.lock != null) ? o.lock : 0;
    S.lastT[id] = -1;
  }

  /** The single clip that currently dominates (or the loco clip). */
  function effectiveClip(id) {
    if (S.mode[id] === 1) return S.clipA[id];
    return S.locoW[id] > 0.5 ? S.locoB[id] : S.locoA[id];
  }
  function effectiveTime(id) {
    if (S.mode[id] === 1) return S.tA[id];
    const c = effectiveClip(id);
    if (c === S.idle[id]) return S.idleT[id];
    return S.phase[id] * CLIPS[c].dur;
  }

  function toLoco(id, blend) {
    if (S.mode[id] === 0) return;
    S.clipB[id] = S.clipA[id]; S.tB[id] = S.tA[id];
    S.fade[id] = 0; S.fadeDur[id] = blend ?? 0.18;
    S.mode[id] = 0; S.hold[id] = 0;
  }

  /* ---------------------------------------------------- state -> clip map */

  function stateClip(w, id) {
    const e = w.entities, st = e.state[id], u = e.spawnKey[id];
    switch (st) {
      case ST.DEAD: {
        const h = hash32(u) & 3;
        return h === 0 ? C.deathB : h === 1 ? C.deathLaunch : C.deathA;
      }
      case ST.HIT: return e.stagger[id] > 0.4 ? C.hitHeavy : C.hitLight;
      case ST.STUN: return C.hitHeavy;
      case ST.LAUNCH: return C.launch;
      case ST.AIRBORNE: return C.airborne;
      case ST.DOWN: return C.knockdown;
      case ST.GETUP: return C.getup;
      case ST.BLOCK: return C.block;
      case ST.GUARD_BREAK: return C.guardBreak;
      case ST.DODGE: return C.dodgeRoll;
      case ST.CHARGE: return C.attackCharge;
      case ST.TAUNT: return C.taunt;
      case ST.CELEBRATE: return C.celebrate;
      case ST.MOUNTED: return C.mounted;
      case ST.WINDUP: case ST.ATTACK: case ST.RECOVER: {
        const n = w.player && id === w.playerId ? (w.player.combo | 0) : (hash32(u + (e.hitToken[id] | 0)) & 3);
        return ATTACK_CHAIN[n % 4];
      }
      default: return -1;                       // -1 => locomotion blend tree
    }
  }

  /* ------------------------------------------------------------ loco tree */

  function updateLoco(w, id, dt) {
    const e = w.entities;
    const sp = Math.max(0, e.speed[id] || Math.hypot(e.vx[id], e.vz[id]));
    let i = 0, f = 0;
    if (sp <= 0.25) { i = 0; f = 0; }
    else if (sp < LOCO_SPEED[1]) { i = 0; f = Math.pow(clamp01((sp - 0.25) / (LOCO_SPEED[1] - 0.25)), 0.55); }
    else if (sp < LOCO_SPEED[2]) { i = 1; f = clamp01((sp - LOCO_SPEED[1]) / (LOCO_SPEED[2] - LOCO_SPEED[1])); }
    else { i = 2; f = clamp01((sp - LOCO_SPEED[2]) / (LOCO_SPEED[3] - LOCO_SPEED[2])); }
    S.locoA[id] = i === 0 ? S.idle[id] : LOCO[i]; S.locoB[id] = LOCO[i + 1]; S.locoW[id] = f;

    // ---- gait phase --------------------------------------------------
    // The ladder is blended on FOOT GEOMETRY, never on the declared speed.
    // The pose actually on screen has a planted-foot excursion of
    // lerp(Ea, Eb) metres held for lerp(ca, cb) of the cycle, so the only
    // stride that keeps that foot nailed to the ground is their ratio —
    // and it stays exact at every point of a walk<->run<->sprint blend,
    // which lerping the two declared strides does not. Body scale goes in
    // too: a 1.94 m general's legs cover more ground per cycle than a
    // 1.74 m archer's.
    const ca = CLIPS[S.locoA[id]], cb = CLIPS[LOCO[i + 1]];
    const ex = Math.max(0.12, ca.excursion + (cb.excursion - ca.excursion) * f);
    const kA = ca.contact || 1, kB = cb.contact || 1;   // idle never leaves the ground
    const stride = ex / (kA + (kB - kA) * f) * (LEG_SCALE[e.kind[id]] * (e.scale[id] || 1));
    const cyc = stride > 0.05 ? Math.max(0.15, Math.min(6, sp / stride)) : (1 / cb.dur) * 0.55;
    S.phase[id] = (S.phase[id] + cyc * dt) % 1;
    S.idleT[id] = (S.idleT[id] + dt * (id === w.playerId ? 1 : S.jit[id])) % CLIPS[S.idle[id]].dur;
  }

  /* -------------------------------------------------------------- events */

  function fireEvents(w, id, clip, t0, t1) {
    const evs = clip.events;
    if (!evs.length) return;
    if (t1 < t0) {                              // looped this step
      fireRange(w, id, evs, t0, clip.dur + 1e-4);
      fireRange(w, id, evs, -1e-4, t1);
    } else fireRange(w, id, evs, t0, t1);
  }
  function fireRange(w, id, evs, t0, t1) {
    for (let i = 0; i < evs.length; i++) {
      const m = evs[i];
      if (m.t > t0 && m.t <= t1) {
        w.bus.emit('anim:event', id, m);
        for (let k = 0; k < listeners.length; k++) listeners[k](id, m);
      }
    }
  }

  /* ------------------------------------------------ presentation autopilot
   * The AI layer currently only ever asks for IDLE or RUN. A field of 500
   * warriors all standing in the same idle is the single most obvious "tech
   * demo" tell, so — until the AI drives it — the animator gives idle units a
   * deterministic combat pantomime. Switch off with `anim.autoDrive = false`.
   */
  function autoDrive(w, id, dt) {
    const e = w.entities;
    if (id === w.playerId) return;
    if (S.mode[id] === 1) return;
    if (e.state[id] !== ST.IDLE && e.state[id] !== ST.WALK) return;
    S.autoT[id] -= dt;
    if (S.autoT[id] > 0) return;
    const u = e.spawnKey[id];
    const foe = e.nearest(e.x[id], e.z[id], 13, e.team[id] === 0 ? 1 : 0, id);
    const r = frac(u * 2654435761 + ((w.time.tick / 37) | 0));
    const r2 = frac(u * 40503 + ((w.time.tick / 53) | 0) * 7919 + 17);
    const r3 = frac(u * 2246822519 + ((w.time.tick / 29) | 0) * 104729 + 41);
    // Temperament is FIXED per unit: some men in a line swing at anything that
    // moves and some hold the rank all battle. Rolling the same distribution
    // for everyone every time makes the whole mass behave identically on
    // average, which is the crowd-scale version of the synchronised-frame tell.
    const heat = frac(u * 374761393 + 907);
    let d2 = 1e9;
    if (foe >= 0) { const dx = e.x[foe] - e.x[id], dz = e.z[foe] - e.z[id]; d2 = dx * dx + dz * dz; }
    if (d2 < 9.6) {
      // in reach: swing at him
      start(id, r3 < 0.16 ? C.attackHeavy : ATTACK_CHAIN[hash32(u + w.time.tick) % 4], { blend: 0.1 });
      S.autoT[id] = 0.34 + r * 0.5;
    } else if (foe >= 0) {
      // Enemy in sight. A battle line is never at rest: most of it holds guard,
      // but at any instant a third of it is mid-swing at whoever is in front,
      // some are braced behind a shield, some are reeling from a blow they just
      // took and the odd man is shouting. Without this the mid-field reads as a
      // parade ground instead of a battle.
      const p = r * 0.7 + heat * 0.3;             // aggressive units swing more
      if (p < 0.36) {
        start(id, r3 < 0.13 ? C.attackHeavy : ATTACK_CHAIN[(hash32(u * 3 + w.time.tick) >>> 3) % 4], { blend: 0.13 });
        S.autoT[id] = 0.5 + r2 * 0.8;
      } else if (p < 0.50) {
        start(id, r3 < 0.35 ? C.blockImpact : C.block, { blend: 0.18, hold: 0.5 + r2 * 1.2 });
        S.autoT[id] = 0.8 + r2 * 1.1;
      } else if (p < 0.60) {
        // took one on the shoulder: a mass with nobody reeling in it is a drill
        start(id, r3 < 0.3 ? C.hitHeavy : C.hitLight, { blend: 0.08 });
        S.autoT[id] = 0.7 + r2 * 1.0;
      } else if (p < 0.68) {
        start(id, C.taunt, { blend: 0.2 });
        S.autoT[id] = CLIPS[C.taunt].dur + r2 * 1.0;
      } else {
        start(id, C.idleAlert, { blend: 0.22, hold: 1.1 + r2 * 2.2 });
        restance(id, r3);
        S.autoT[id] = 1.1 + r2 * 2.2;
      }
    } else if (r < 0.46) {
      const c = r < 0.07 ? C.celebrate : r < 0.15 ? C.taunt : r < 0.22 ? C.turnL : r < 0.29 ? C.turnR : C.idleAlert;
      start(id, c, { blend: 0.24, hold: c === C.idleAlert ? 1.4 + r2 * 2.6 : CLIPS[c].dur });
      restance(id, r3);
      S.autoT[id] = 1.8 + r2 * 3.4;
    } else {
      S.autoT[id] = 0.9 + r2 * 2.2;
    }
  }

  /**
   * Re-roll which standing stance a unit falls back to.
   *
   * The stance is the single biggest thing separating one silhouette from
   * another in a static rank, and a stance chosen once at spawn means the
   * field is permanently sorted into as many poses as there are IDLES.
   *
   * Call this AFTER the start() that hands the body to a one-shot clip, never
   * before and never on its own: start() captures the stance that is currently
   * on screen as the cross-fade source, so re-rolling first would swap the
   * pose in the same frame. Re-rolled after, the new stance is not seen until
   * toLoco() fades back into it when the one-shot ends.
   */
  function restance(id, r) {
    if (r > 0.34) return;
    const n = IDLES.length;
    const k = IDLES[(hash32(S.spawnKey[id] * 2654435761 + (S.idle[id] | 0) * 7919 + 3) >>> 5) % n];
    if (k === S.idle[id]) return;
    S.idle[id] = k;
    S.idleT[id] = frac(S.spawnKey[id] * 104729 + (k | 0) * 31 + 7) * CLIPS[k].dur;
  }

  /* ------------------------------------------------------------- posing */

  /** Fill qO/pO with the blended local pose of entity `id`. */
  function evalPose(id) {
    api.sampleInstance(id, _out5);
    const ca = CLIPS[_out5[0]], cb = CLIPS[_out5[2]], wB = _out5[4];
    sampleClip(ca, _out5[1], eulA, posA);
    eulerToQuat(eulA, qA);
    wristCompensate(qA);
    if (wB > 0.001) {
      sampleClip(cb, _out5[3], eulB, posB);
      eulerToQuat(eulB, qB);
      wristCompensate(qB);
      blendQ(qA, qB, wB, qO);
      blendP(posA, posB, wB, pO);
    } else { qO.set(qA); pO.set(posA); }
  }

  /** Additive layers: breathing + hit flinch. Written straight onto qO. */
  function additive(id, t) {
    const br = Math.sin(t * 1.9) * 0.5 + 0.5;
    const fl = S.flinch[id];
    eulD.fill(0);
    eulD[BONE_INDEX.spine2 * 3] = -0.020 * br;
    eulD[BONE_INDEX.chest * 3] = -0.028 * br;
    eulD[BONE_INDEX.clavicleL * 3 + 2] = -0.030 * br;
    eulD[BONE_INDEX.clavicleR * 3 + 2] = 0.030 * br;
    eulD[BONE_INDEX.neck * 3] = 0.012 * br;
    if (fl > 0.001) {
      const o = Math.sin(t * 52) * fl;
      eulD[BONE_INDEX.chest * 3] += -0.26 * fl;
      eulD[BONE_INDEX.chest * 3 + 2] += o * 0.16;
      eulD[BONE_INDEX.head * 3] += -0.34 * fl;
      eulD[BONE_INDEX.head * 3 + 1] += o * 0.24;
      eulD[BONE_INDEX.spine1 * 3] += -0.12 * fl;
    }
    eulerToQuat(eulD, qD);
    addQ(qO, qD, 1, qO, ADD_MASK);
  }

  function poseHero(w, dt) {
    const id = w.playerId;
    if (id < 0 || !heroBones) return;
    evalPose(id);
    additive(id, breatheT);
    const rig = heroRig;
    const E = w.entities;
    heroMesh.position.set(E.x[id], E.y[id], E.z[id]);
    heroMesh.rotation.set(0, E.yaw[id], 0);
    for (let b = 0; b < BONE_COUNT; b++) {
      const bone = heroBones[b];
      bone.quaternion.set(qO[b * 4], qO[b * 4 + 1], qO[b * 4 + 2], qO[b * 4 + 3]);
      const o3 = b * 3;
      if (b === 0) {
        bone.position.set(
          rig.pos[0] + (api.applyRootMotion ? pO[0] : 0),
          rig.pos[1] + pO[1],
          rig.pos[2] + (api.applyRootMotion ? pO[2] : 0));
      } else if (pO[o3] || pO[o3 + 1] || pO[o3 + 2]) {
        bone.position.set(rig.pos[o3] + pO[o3], rig.pos[o3 + 1] + pO[o3 + 1], rig.pos[o3 + 2] + pO[o3 + 2]);
      } else {
        bone.position.set(rig.pos[o3], rig.pos[o3 + 1], rig.pos[o3 + 2]);
      }
    }
    heroBones[0].updateMatrixWorld(true);

    // look-at: the hero keeps his eyes on whatever he is fighting
    const e = w.entities;
    const tgt = e.target[id];
    if (api.lookAtWeight > 0) {
      if (tgt >= 0 && e.alive[tgt]) _look.set(e.x[tgt], e.y[tgt] + 1.45, e.z[tgt]);
      else _look.set(e.x[id] + Math.sin(e.yaw[id]) * 8, e.y[id] + 1.7, e.z[id] + Math.cos(e.yaw[id]) * 8);
      lookAt(heroBones, _look, api.lookAtWeight);
      heroBones[0].updateMatrixWorld(true);
    }
    if (api.footIK > 0 && w.terrain && e.state[id] !== ST.DEAD && e.state[id] !== ST.DOWN) {
      plantFeet(heroMesh, heroBones, w.terrain, { weight: api.footIK });
    }
  }

  /* ============================================================== SYSTEM */

  return {
    name: 'anim', order: ORDER.ANIM,

    init(w) {
      W = w;
      assets = animAssets(w);
      for (let i = 0; i < BONE_COUNT; i++) ADD_MASK[i] = 1;
      for (let i = 0; i < 2600; i++) reset(i, LOCO[0]);

      w.bus.on('hit', (id, src, amount, opts) => {
        if (id >= 0 && id < 2600) { S.flinch[id] = Math.min(1, S.flinch[id] + 0.75); }
      });

      api = {
        ready: true,
        clips: CLIPS, clipNames: CLIP_NAMES, clipIndex: CLIP_INDEX, count: CLIP_COUNT,
        C, LOCO, state: S,
        vat: assets.vat, rigs: assets.rigs, kindToVat: KIND_TO_VAT, vatKinds: VAT_KIND_NAMES,
        autoDrive: true,
        applyRootMotion: true,
        footIK: 1,
        lookAtWeight: 0.45,

        clipId(name) { const i = CLIP_INDEX[name]; return i === undefined ? -1 : i; },
        duration(n) { const c = CLIPS[typeof n === 'number' ? n : CLIP_INDEX[n]]; return c ? c.dur : 0; },
        events(n) { const c = CLIPS[typeof n === 'number' ? n : CLIP_INDEX[n]]; return c ? c.events : EMPTY; },
        clip(n) { return CLIPS[typeof n === 'number' ? n : CLIP_INDEX[n]] || null; },

        play(id, name, opts) {
          const ci = typeof name === 'number' ? name : CLIP_INDEX[name];
          if (ci === undefined || id < 0) return false;
          w.entities.anim[id] = ci;
          const o = opts || EMPTY_OPTS;
          start(id, ci, { blend: o.blend, rate: o.rate, hold: o.hold,
            lock: o.lock != null ? o.lock : (CLIPS[ci].loop ? 0 : CLIPS[ci].dur * 0.98) });
          return true;
        },
        crossFade(id, name, t) { return api.play(id, name, { blend: t }); },
        /** Freeze an entity on an exact frame of a clip — used by capture tools. */
        scrub(id, name, t01) {
          const ci = typeof name === 'number' ? name : CLIP_INDEX[name];
          if (ci === undefined || id < 0) return false;
          S.clipA[id] = ci; S.clipB[id] = ci;
          S.tA[id] = CLIPS[ci].dur * Math.max(0, Math.min(1, t01));
          S.tB[id] = S.tA[id];
          S.fade[id] = 1; S.mode[id] = 1; S.rate[id] = 0;
          S.hold[id] = 1e9; S.lock[id] = 1e9; S.lastT[id] = -1;
          w.entities.anim[id] = ci;
          return true;
        },
        unscrub(id) { S.rate[id] = 1; S.lock[id] = 0; S.hold[id] = 0; },
        stop(id) { toLoco(id, 0.2); },
        speedFor(id) { return S.rate[id] * (W.entities.animSpeed[id] || 1); },
        /** Foot-lock telemetry: what the lock asked for vs what the solver hit. */
        ikStats: IK_STATS,

        /** [clipA, tA01, clipB, tB01, blendB] — the crowd shader's input. */
        sampleInstance(id, out) {
          const mode = S.mode[id], fade = S.fade[id];
          let a, ta, b, tb, wB;
          if (mode === 1) {
            a = S.clipA[id]; ta = CLIPS[a].dur > 0 ? S.tA[id] / CLIPS[a].dur : 0;
            if (CLIPS[a].loop) ta -= Math.floor(ta); else ta = ta > 1 ? 1 : ta;
          } else {
            a = S.locoA[id];
            ta = a === S.idle[id] ? (S.idleT[id] / CLIPS[a].dur) % 1 : S.phase[id];
          }
          if (fade < 1) {
            b = S.clipB[id];
            tb = CLIPS[b].dur > 0 ? S.tB[id] / CLIPS[b].dur : 0;
            if (CLIPS[b].loop) tb -= Math.floor(tb); else tb = tb > 1 ? 1 : tb;
            wB = 1 - fade;
            // while cross-fading, collapse the loco pair onto its dominant clip
            if (mode === 0 && S.locoW[id] > 0.5) { a = S.locoB[id]; ta = S.phase[id]; }
          } else if (mode === 0 && S.locoW[id] > 0.001) {
            b = S.locoB[id];
            tb = S.phase[id];
            if (a === S.idle[id]) ta = (S.idleT[id] / CLIPS[a].dur) % 1;
            wB = S.locoW[id];
            const kA = CLIPS[a].contact, kB = CLIPS[b].contact;
            if (kA > 0 && kB > 0) {
              const kr = kA + (kB - kA) * wB;
              ta = gaitWarp(ta, kA, kr);
              tb = gaitWarp(tb, kB, kr);
            }
          } else { b = a; tb = ta; wB = 0; }
          out[0] = a; out[1] = ta < 0 ? 0 : ta; out[2] = b; out[3] = tb < 0 ? 0 : tb; out[4] = wB;
          return out;
        },

        /** Blended skinning matrices for one entity (21 * 12 floats). */
        sample(id, outMat) {
          evalPose(id);
          const kind = assets.rigs[VAT_KIND_NAMES[KIND_TO_VAT[W.entities.kind[id]]]];
          poseToMatrices(qO, pO, kind, mWorld, outMat || mSkin);
          return outMat || mSkin;
        },
        /** Local pose buffers of the last sample() / hero pose. */
        poseBuffers: { q: qO, p: pO, world: mWorld },

        rootMotion(id, out) {
          if (out) { out.x = S.rmX[id]; out.y = 0; out.z = S.rmZ[id]; return out; }
          _v3.set(S.rmX[id], 0, S.rmZ[id]); return _v3;
        },
        onEvent(cb) { listeners.push(cb); return () => { const i = listeners.indexOf(cb); if (i >= 0) listeners.splice(i, 1); }; },
        flinch(id, amount) { S.flinch[id] = Math.min(1.4, S.flinch[id] + amount); },
        reset,
        resetBattle,
        stats: { bakeMs: assets.vat.bakeMs, buildMs: assets.buildMs, clips: CLIP_COUNT,
          frames: assets.vat.totalFrames, mb: assets.vat.megabytes,
          tex: assets.vat.width + 'x' + assets.vat.height },
      };
      w.anim = api;
      console.log('[anim]', CLIP_COUNT, 'clips,', assets.vat.totalFrames, 'VAT frames,',
        assets.vat.width + 'x' + assets.vat.height, assets.vat.megabytes + ' MB, bake',
        assets.vat.bakeMs + 'ms');
    },

    fixedUpdate(w, dt) {
      const e = w.entities;
      for (let i = 0; i < e.liveN; i++) {
        const id = e.live[i];
        if (S.uid[id] !== e.uid[id] || S.spawnKey[id] !== e.spawnKey[id]) {
          S.uid[id] = e.uid[id];
          S.spawnKey[id] = e.spawnKey[id];
          reset(id, LOCO[0]);
        }

        // flinch decay
        if (S.flinch[id] > 0) S.flinch[id] = Math.max(0, S.flinch[id] - dt * 3.4);

        // 1. what does the gameplay state want?
        const rate = e.animSpeed[id] || 1;
        if (S.lock[id] > 0) S.lock[id] -= dt;
        const want = S.lock[id] > 0 ? (S.mode[id] === 1 ? S.clipA[id] : -1) : stateClip(w, id);
        if (want < 0) {
          updateLoco(w, id, dt * rate);
          if (S.mode[id] === 1) {
            S.hold[id] -= dt * rate;
            const c = CLIPS[S.clipA[id]];
            const done = !c.loop && S.tA[id] >= c.dur - 1e-4;
            const ran = S.lock[id] <= 0 && (e.speed[id] > 3.2) && c.tags === 'attack';
            if (S.hold[id] <= 0 || done || ran) toLoco(id, Math.min(0.24, c.blend + 0.06));
          } else if (api.autoDrive) autoDrive(w, id, dt);
        } else if (S.mode[id] !== 1 || S.clipA[id] !== want) {
          start(id, want, null);
          e.prevAnim[id] = want;
        }

        // 2. advance the clocks (single clips get the per-unit jitter; the
        //    locomotion phase never does — it is locked to ground speed)
        const step = dt * rate * S.rate[id] * (id === w.playerId ? 1 : S.jit[id]);
        if (S.mode[id] === 1) {
          const c = CLIPS[S.clipA[id]];
          const t0 = S.tA[id];
          let t1 = t0 + step;
          if (c.loop) { if (t1 >= c.dur) t1 -= c.dur; }
          else if (t1 > c.dur) t1 = c.dur;
          S.tA[id] = t1;
          if (t1 !== t0) fireEvents(w, id, c, t0, t1);
          // root motion for gameplay consumers
          if (c.pos[0]) {
            const dz = c.rm * (t1 - t0) / Math.max(1e-4, c.dur);
            const s = Math.sin(e.yaw[id]), co = Math.cos(e.yaw[id]);
            S.rmX[id] = s * dz; S.rmZ[id] = co * dz;
          } else { S.rmX[id] = 0; S.rmZ[id] = 0; }
        } else {
          S.rmX[id] = 0; S.rmZ[id] = 0;
          const c = CLIPS[effectiveClip(id)];
          const t0 = S.lastT[id], t1 = effectiveTime(id);
          if (t0 >= 0 && Math.abs(t1 - t0) > 1e-5) fireEvents(w, id, c, t0, t1);
          S.lastT[id] = t1;
        }
        if (S.fade[id] < 1) {
          S.tB[id] += step;
          const cb = CLIPS[S.clipB[id]];
          if (cb.loop && S.tB[id] >= cb.dur) S.tB[id] -= cb.dur;
          S.fade[id] = Math.min(1, S.fade[id] + dt / Math.max(0.01, S.fadeDur[id]));
        }
        e.animT[id] = S.mode[id] === 1 ? S.tA[id] : S.phase[id] * 1.0;
        e.anim[id] = S.mode[id] === 1 ? S.clipA[id] : effectiveClip(id);
      }
    },

    update(w, dt, alpha, realDt) {
      breatheT += realDt || dt;
      if (!heroBones && w.chars?.playerBones) {
        heroBones = w.chars.playerBones;
        heroMesh = w.chars.player;
        heroRig = heroMesh?.userData?.rig || assets.rigs.hero;
      }
    },

    lateUpdate(w, dt, alpha, realDt) {
      if (heroBones) poseHero(w, realDt || dt);
    },
  };
}

const EMPTY = Object.freeze([]);
const EMPTY_OPTS = Object.freeze({});
